/**
 * instrumentation-client.ts
 *
 * This file runs in the browser BEFORE React hydrates and BEFORE user interaction.
 * Use it to:
 *   - Initialize error handlers for uncaught errors and unhandled promise rejections
 *   - Place early performance marks to measure hydration and navigation
 *   - Optionally track SPA navigations via onRouterTransitionStart
 *   - Send lightweight, sampled telemetry to the server without blocking the UI
 *
 * Important constraints:
 *   - Keep work tiny (<16ms) to avoid delaying hydration
 *   - Never block rendering; prefer fire-and-forget and navigator.sendBeacon/keepalive
 *   - Sample to avoid excessive logs
 */

// Mark the earliest possible client start for later measurements (e.g., hydration)
performance.mark('app-init')

// ---- Configuration knobs (tweak as you like) ----
const ERROR_SAMPLE_RATE = 1.0 // 100% of errors (errors are rare; keep full fidelity)
const PERF_SAMPLE_RATE = 20 // 5% of sessions for performance to limit volume
const OBS_ENDPOINT_ERROR = '/api/observability/error'
const OBS_ENDPOINT_PERF = '/api/observability/perf'

// Generate a stable session id to correlate multiple beacons from the same tab
const sessionId = (() => {
    try {
        const key = '__blend_obs_session__'
        const existing = sessionStorage.getItem(key)
        if (existing) return existing
        const id = Math.random().toString(36).slice(2)
        sessionStorage.setItem(key, id)
        return id
    } catch {
        return Math.random().toString(36).slice(2)
    }
})()

// ---- Error capture (uncaught only). Your try/catch logs are separate. ----
// To avoid duplicate logs across multiple handlers, we de-dupe by message+stack within this session.
const seenErrors = new Set<string>()

function fingerprintError(errLike: unknown): string {
    if (errLike && typeof errLike === 'object') {
        const e = errLike as Record<string, any>
        return `${e.name || 'Error'}|${e.message || ''}|${e.stack || ''}`
    }
    return `non-error|${String(errLike)}`
}

function sendErrorBeacon(payload: Record<string, any>) {
    try {
        const body = JSON.stringify(payload)
        // Prefer sendBeacon to avoid blocking unload; fall back to fetch keepalive
        const blob = new Blob([body], { type: 'application/json' })
        const ok = navigator.sendBeacon?.(OBS_ENDPOINT_ERROR, blob)
        if (!ok) {
            fetch(OBS_ENDPOINT_ERROR, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                keepalive: true
            }).catch(() => {})
        }
    } catch {
        // never throw from instrumentation
    }
}

function reportClientError(errLike: unknown, context?: Record<string, any>) {
    if (Math.random() > ERROR_SAMPLE_RATE) return
    try {
        const fp = fingerprintError(errLike)
        if (seenErrors.has(fp)) return
        seenErrors.add(fp)
        const e = errLike instanceof Error ? errLike : new Error(String(errLike))
        sendErrorBeacon({
            sessionId,
            source: 'client',
            message: e.message,
            name: e.name,
            stack: e.stack,
            context: {
                url: location.href,
                userAgent: navigator.userAgent,
                ...context
            },
            ts: Date.now()
        })
    } catch {
        // swallow
    }
}

// Global listeners catch uncaught errors only (won't double-log caught errors unless rethrown)
window.addEventListener('error', ev => {
    // ev.error is usually the Error instance; ev.message is a string fallback
    reportClientError(ev.error ?? ev.message, { type: 'window.error' })
})

window.addEventListener('unhandledrejection', ev => {
    reportClientError(ev.reason, { type: 'unhandledrejection' })
})

// ---- Performance collection ----
// We create named measures using performance.mark() and performance.measure().
// A small sampler controls volume. Measures are batched and sent during idle/unload.

const perfSampled = Math.random() < PERF_SAMPLE_RATE

function collectMeasures(): Array<{ name: string, duration: number, startTime: number }> {
    const entries = performance.getEntriesByType('measure') as PerformanceMeasure[]
    return entries.map(m => ({ name: m.name, duration: m.duration, startTime: m.startTime }))
}

function flushPerf(reason: string) {
    if (!perfSampled) return
    try {
        const measures = collectMeasures()
        if (!measures.length) return
        const body = JSON.stringify({
            sessionId,
            reason,
            measures,
            url: location.href,
            ts: Date.now()
        })
        const blob = new Blob([body], { type: 'application/json' })
        const ok = navigator.sendBeacon?.(OBS_ENDPOINT_PERF, blob)
        if (!ok) {
            fetch(OBS_ENDPOINT_PERF, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                keepalive: true
            }).catch(() => {})
        }
    } catch {
        // swallow
    }
}

// On tab hide/unload, try to flush any pending measures
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPerf('visibilitychange')
})

// ---- SPA navigation hook ----
// Next.js will call this when a router transition starts (App Router).
// We mark the start and let you create an end mark elsewhere (e.g., after content settles).
export function onRouterTransitionStart(url: string, navigationType: 'push' | 'replace' | 'traverse') {
    try {
        performance.mark(`nav-start`)
        // Optionally: you could end the nav measure in a client component when content is ready
    } catch {
        // ignore
    }
}


